import {ThunkResult} from "../../types";
import {
    getAesSignature,
    getAesSignatures,
    getDataSignatures,
    getHtmlDataSignature,
    getHtmlSignatures,
    getPdfSignature,
    getPdfSignatures,
    getTimestamps,
    postTimestamp
} from "../../api/signatures";
import {GetParams} from "@kopjra/uikit";
import {
    gotAesJson,
    gotAesSignatures,
    gotDataJson,
    gotDataSignatures,
    gotHTMLJson,
    gotHtmlSignatures,
    gotPdfJson,
    gotPdfSignatures,
    gotTimestamps
} from "../signatures";
import {
    createDownload,
    transformToAesSignatureParams,
    transformToBasicSignatureParams,
    transformToPdfSignatureParams,
    transformToTimestampParams
} from "../../utils/commons";
import {HtmlSignature} from "../../types/htmlSignatures";
import {DataSignature} from "../../types/dataSignatures";
import {AesSignature, AesSignatureFull, PdfSignature, PdfSignatureFull} from "../../types/pdfSignatures";
import JSZip from "jszip";

export const doGetHtmlSignatures = (query: GetParams): ThunkResult<Promise<void>> => async (dispatch) => {
    dispatch(gotHtmlSignatures(await getHtmlSignatures(transformToBasicSignatureParams(query, "full"))));
}

export const doDownloadHtmlDataXML = (sig: HtmlSignature | DataSignature): ThunkResult<Promise<void>> => async () => {
    const fullSig = await getHtmlDataSignature(sig.id);
    if (fullSig.attachedFiles) {
        createDownload(`${sig.id}.xml`, fullSig.attachedFiles.xmlUrl);
    }
}

export const doGetDataSignatures = (query: GetParams): ThunkResult<Promise<void>> => async (dispatch) => {
    dispatch(gotDataSignatures(await getDataSignatures(transformToBasicSignatureParams(query, "no_html"))));
}

export const doGetPdfSignatures = (query: GetParams, isForensic: boolean): ThunkResult<Promise<void>> => async (dispatch) => {
    dispatch(gotPdfSignatures(await getPdfSignatures(transformToPdfSignatureParams(query, isForensic))));
}

export const doGetAesSignatures = (query: GetParams): ThunkResult<Promise<void>> => async (dispatch) => {
    dispatch(gotAesSignatures(await getAesSignatures(transformToAesSignatureParams(query))));
}

export const doDownloadPdfXML = (sig: PdfSignature): ThunkResult<Promise<void>> => async () => {
    const fullSig = await getPdfSignature(sig.id);
    if (fullSig.attachedFiles) {
        createDownload(`${sig.id}.xml`, fullSig.attachedFiles.xml.url);
    }
}

export const doDownloadAESXML = (sig: AesSignature): ThunkResult<Promise<void>> => async () => {
    const fullSig = await getAesSignature(sig.id, sig.aesProviderId);
    if (fullSig.attachedFiles) {
        createDownload(`${sig.id}.xml`, fullSig.attachedFiles.xml.url);
    }
}

async function downloadPDForZip(fullSig: PdfSignatureFull | AesSignatureFull, sig: PdfSignature) {
    if (fullSig.attachedFiles) {
        if (fullSig.attachedFiles.signedDocuments.length === 1) {
            const res = await fetch(fullSig.attachedFiles.signedDocuments[0].url);
            createDownload(`${sig.id}-${fullSig.attachedFiles.signedDocuments[0].name}${fullSig.attachedFiles.signedDocuments[0].name.endsWith(".pdf") ? "" : ".pdf"}`, await res.blob());
        } else if (fullSig.attachedFiles.signedDocuments.length > 1) {
            const zip = new JSZip();
            await Promise.all(fullSig.attachedFiles.signedDocuments.map(async (document) => {
                const res = await fetch(document.url);
                const data = await res.blob();
                zip.file(`${document.name}${document.name.endsWith(".pdf") ? "" : ".pdf"}`, data);
            }));

            const content = await zip.generateAsync({type: "blob"});
            createDownload(`${sig.id}.zip`, content);
        }
    }
}

export const doDownloadPdf = (sig: PdfSignature): ThunkResult<Promise<void>> => async () => {
    const fullSig = await getPdfSignature(sig.id);
    await downloadPDForZip(fullSig, sig);
}

export const doDownloadAes = (sig: AesSignature): ThunkResult<Promise<void>> => async () => {
    const fullSig = await getAesSignature(sig.id, sig.aesProviderId);
    await downloadPDForZip(fullSig, sig);
}

export const doGetHtmlJson = (sig: HtmlSignature): ThunkResult<Promise<void>> => async (dispatch) => {
    const fullSig = await getHtmlDataSignature(sig.id);
    dispatch(gotHTMLJson(fullSig));
}

export const doGetDataJson = (sig: DataSignature): ThunkResult<Promise<void>> => async (dispatch) => {
    const fullSig = await getHtmlDataSignature(sig.id);
    dispatch(gotDataJson(fullSig));
}

export const doGetPdfJson = (sig: PdfSignature): ThunkResult<Promise<void>> => async (dispatch) => {
    const fullSig = await getPdfSignature(sig.id);
    dispatch(gotPdfJson(fullSig));
}

export const doGetAesJson = (sig: AesSignature): ThunkResult<Promise<void>> => async (dispatch) => {
    const fullSig = await getAesSignature(sig.id, sig.aesProviderId);
    dispatch(gotAesJson(fullSig));
}

export const doTimestamp = (file: File, trusted: boolean): ThunkResult<Promise<void>> => async () => {
    await postTimestamp(file, trusted);
}

export const doGetTimestamps = (query: GetParams): ThunkResult<Promise<void>> => async (dispatch) => {
    dispatch(gotTimestamps(await getTimestamps(transformToTimestampParams(query))));
}
